/**
 * Card-to-job-page morph for the B2 SRP card (approved mockup: "Click a title or Katso: the card
 * grows into the job page, the brand strip becomes the left rail. Back reverses it.").
 *
 * Every internal link in this app (see Link.tsx) navigates through Vike's client router
 * (`navigate()`, pushState-based), not a real document navigation -- including SRP -> job page.
 * So the primary mechanism here is a SAME-document View Transition, driven manually from
 * +onPageTransitionStart.ts / +onPageTransitionEnd.ts (startSpaJobCardTransition /
 * resolveSpaJobCardTransition below), since `document.startViewTransition()` doesn't get invoked
 * by the browser automatically the way it does for a real navigation.
 * - A primary click on a card's title or View link calls armJobCardTransition(), which marks that
 *   one card (.jc-vt-on) so only it carries the shared view-transition-names (see
 *   job-card-transition.css) and stamps the time for the guard. This runs synchronously in the
 *   click handler, before Link.tsx calls navigate(), so the .jc-vt-on class is already in the DOM
 *   when +onPageTransitionStart's hook (and therefore startSpaJobCardTransition) fires and takes
 *   the "old" snapshot.
 * - The job page container always carries .jc-vt-on, so its rail, logo and title are the targets
 *   for the "new" snapshot once the job page has actually mounted (+onPageTransitionEnd fires
 *   after Vike's render, not before).
 * - Going back (JDP -> SRP) is also a soft nav; Vike exposes `pageContext.isBackwardNavigation`
 *   (client-routing only) instead of the traverse-navigation checks a cross-document transition
 *   would need. rearmSrpCardFromStorage() re-applies .jc-vt-on to the matching SRP card (found via
 *   sessionStorage, same lookup the cross-document guard below does for pagereveal) once the SRP
 *   has re-mounted, just before resolving the transition.
 *
 * - Skeleton-first (job-preview-shell.ts): an armed card click also registers what the card shows
 *   (title, company, logo, brand colour). When that seed matches the destination, the transition's
 *   update callback mounts the JobPreviewShell overlay and resolves at once, so the morph runs on
 *   click instead of after the server round trip; the real page renders under the shell and
 *   replaces it. Without a seed (other job links, Back) the callback waits for
 *   +onPageTransitionEnd as before. +onRenderClient waits (waitForJobCardTransitionCapture) until
 *   the old snapshot has been taken, since prefetched data can make the next page ready first.
 *
 * JobCardTransitionHead / the pageswap+pagereveal guard script below still exist for the one case
 * where a REAL cross-document navigation happens (e.g. a route this app ever hard-navigates to via
 * requiresFullPageLoad(), or a pre-hydration native <a> click) -- harmless to leave in place, and
 * it shares the same view-transition-name CSS.
 */

import { mountJobPreviewShell, reserveJobPreviewShell, takeJobPreviewSeedFor } from './job-preview-shell';

export const JOB_CARD_TRANSITION_SOURCE_CLASS = 'jc-vt-on';

/** How long after a card click the guard still treats a navigation as "from that card". */
export const JOB_CARD_TRANSITION_ARM_WINDOW_MS = 5000;

/**
 * sessionStorage keys written by armJobCardTransition and read back on the job page (the
 * "Takaisin tuloksiin" / "Back to results" link, JobDetailPageV2EditorialRail.tsx) and by the
 * inline guard script when the visitor comes Back to the SRP (see
 * JOB_CARD_TRANSITION_GUARD_SCRIPT's pagereveal handler). sessionStorage (not a cookie/URL param)
 * so this only ever applies within the same tab's session and never leaks into a shared link.
 */
export const JOB_CARD_TRANSITION_JOB_ID_STORAGE_KEY = 'jc-vt-job';
export const JOB_CARD_TRANSITION_SRP_URL_STORAGE_KEY = 'jc-vt-srp-url';

type WindowWithArm = Window & { __jcVtArmedAt?: number };

/** Call on a primary click on a B2 card's title or View link, before the browser navigates. */
export function armJobCardTransition(article: Element | null): void {
  if (!article || typeof document === 'undefined') return;
  document.querySelectorAll(`article.${JOB_CARD_TRANSITION_SOURCE_CLASS}`).forEach((el) => el.classList.remove(JOB_CARD_TRANSITION_SOURCE_CLASS));
  article.classList.add(JOB_CARD_TRANSITION_SOURCE_CLASS);
  (window as WindowWithArm).__jcVtArmedAt = Date.now();
  // Best-effort: sessionStorage can throw (private mode, quota, disabled storage) -- the "Back to
  // results" link and the Back-morph fix are both progressive enhancements, so a failure here just
  // means neither shows up, never a broken navigation.
  try {
    const jobId = (article as HTMLElement).dataset?.jobId;
    if (jobId) window.sessionStorage.setItem(JOB_CARD_TRANSITION_JOB_ID_STORAGE_KEY, jobId);
    window.sessionStorage.setItem(JOB_CARD_TRANSITION_SRP_URL_STORAGE_KEY, window.location.href);
  } catch {
    /* ignore */
  }
}

/** The SRP URL armJobCardTransition stored for the card that was clicked, if any, in this tab. */
export function getStoredJobCardTransitionSrpUrl(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(JOB_CARD_TRANSITION_SRP_URL_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * True when the browser's own `document.referrer` for this page load is exactly the SRP URL
 * armJobCardTransition stored -- i.e. that SRP really is the immediate previous history entry,
 * so `history.back()` will restore it (rather than navigating somewhere else). Used by the "Back
 * to results" link: when this is false (the stored URL is stale, or the job page was reached some
 * other way in this tab), it falls back to a plain navigation to the stored URL instead.
 */
export function isStoredSrpUrlTheReferrer(): boolean {
  if (typeof document === 'undefined') return false;
  const stored = getStoredJobCardTransitionSrpUrl();
  return Boolean(stored && document.referrer && document.referrer === stored);
}

/**
 * Inline <head> script: runs before first paint on both documents.
 * - pageswap (old document) keeps the transition only for an armed card click from the SRP, or
 *   a Back (traverse) navigation away from the job page.
 * - pagereveal (new document) drops it for reduced-motion users. On the SRP document, when this
 *   reveal is itself a Back (traverse) navigation, it also re-names the clicked card
 *   (article[data-job-id] matching the id armJobCardTransition stashed in sessionStorage) so the
 *   morph reverses even when the browser didn't restore the SRP from bfcache (a plain
 *   reload-style Back, and always true on the Vite dev server, whose HMR socket makes the page
 *   bfcache-ineligible) -- without this, the new SRP document has no named card and the
 *   transition just crossfades. Runs before React hydrates; hydration never touches this
 *   attribute/class, so it can't race the snapshot. Skipped (no naming) if the card isn't found
 *   or sits entirely outside the viewport after scroll restoration, so the animation never flies
 *   in from off-screen -- falls back to a plain crossfade instead. The class is removed again
 *   once the transition finishes so an unrelated later navigation can't reuse it.
 */
export const JOB_CARD_TRANSITION_GUARD_SCRIPT = `(function(){try{
var rm=function(){return window.matchMedia&&matchMedia('(prefers-reduced-motion: reduce)').matches;};
addEventListener('pageswap',function(e){var vt=e.viewTransition;if(!vt)return;
var onJob=!!document.querySelector('.jc-job-page');
var armed=!onJob&&window.__jcVtArmedAt&&(Date.now()-window.__jcVtArmedAt)<${JOB_CARD_TRANSITION_ARM_WINDOW_MS};
var back=onJob&&e.activation&&e.activation.navigationType==='traverse';
if(rm()||!(armed||back)){vt.skipTransition();}});
addEventListener('pagereveal',function(e){
var vt=e.viewTransition;
if(!vt)return;
if(rm()){vt.skipTransition();return;}
var onJob=!!document.querySelector('.jc-job-page');
if(onJob)return;
var nav=window.navigation&&window.navigation.activation;
var isBack=Boolean(nav&&nav.navigationType==='traverse');
if(!isBack)return;
var storedId=null;
try{storedId=sessionStorage.getItem('${JOB_CARD_TRANSITION_JOB_ID_STORAGE_KEY}');}catch(_){}
if(!storedId)return;
var cards=document.querySelectorAll('article[data-job-id]');
var match=null;
for(var i=0;i<cards.length;i++){if(cards[i].getAttribute('data-job-id')===storedId){match=cards[i];break;}}
if(!match)return;
var r=match.getBoundingClientRect();
var vw=window.innerWidth||document.documentElement.clientWidth;
var vh=window.innerHeight||document.documentElement.clientHeight;
var inView=r.bottom>0&&r.top<vh&&r.right>0&&r.left<vw;
if(!inView)return;
match.classList.add('${JOB_CARD_TRANSITION_SOURCE_CLASS}');
var cleanup=function(){match.classList.remove('${JOB_CARD_TRANSITION_SOURCE_CLASS}');};
vt.finished.then(cleanup,cleanup);
});
}catch(_){}})();`;

type ViewTransitionCapableDocument = Document & {
  startViewTransition?: (callback: () => Promise<void> | void) => ViewTransitionHandle;
};

type ViewTransitionHandle = {
  finished: Promise<void>;
  ready?: Promise<void>;
  skipTransition?: () => void;
};

let pendingResolve: (() => void) | null = null;
let pendingTimeoutId: ReturnType<typeof setTimeout> | null = null;

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

function isArmedWithinWindow(): boolean {
  const armedAt = (window as WindowWithArm).__jcVtArmedAt;
  return typeof armedAt === 'number' && Date.now() - armedAt < JOB_CARD_TRANSITION_ARM_WINDOW_MS;
}

function rearmSrpCardFromStorage(): void {
  let storedId: string | null = null;
  try {
    storedId = window.sessionStorage.getItem(JOB_CARD_TRANSITION_JOB_ID_STORAGE_KEY);
  } catch {
    return;
  }
  if (!storedId) return;
  // Iterate rather than build a `[data-job-id="..."]` selector: avoids depending on
  // CSS.escape (not implemented in jsdom, and storedId is attacker-influenced sessionStorage).
  const cards = document.querySelectorAll('article[data-job-id]');
  let match: Element | null = null;
  for (let i = 0; i < cards.length; i++) {
    if (cards[i].getAttribute('data-job-id') === storedId) {
      match = cards[i];
      break;
    }
  }
  if (!match) return;
  const rect = match.getBoundingClientRect();
  const vw = window.innerWidth || document.documentElement.clientWidth;
  const vh = window.innerHeight || document.documentElement.clientHeight;
  const inView = rect.bottom > 0 && rect.top < vh && rect.right > 0 && rect.left < vw;
  if (!inView) return;
  match.classList.add(JOB_CARD_TRANSITION_SOURCE_CLASS);
}

/**
 * Resolved once the in-flight transition's update callback has started, i.e. once the browser
 * has captured the "old" snapshot. +onRenderClient awaits this before rendering the next page:
 * with prefetched pageContext the next page can be ready within the same frame as the click,
 * and rendering it before the capture would put the destination page into the "old" snapshot.
 */
let captureWait: Promise<void> | null = null;
let captureResolve: (() => void) | null = null;
/** The transition waiting on its capture, so a late one can be skipped (see beginCaptureWait). */
let capturingTransition: ViewTransitionHandle | null = null;
let captureTimeoutId: ReturnType<typeof setTimeout> | null = null;
/** How long the next page's render may wait for the old-snapshot capture. */
const CAPTURE_TIMEOUT_MS = 250;

function beginCaptureWait(): void {
  captureWait = new Promise<void>((resolve) => {
    captureResolve = resolve;
  });
  // The browser needs a frame to capture the old snapshot; long tasks or a slow device can delay
  // it. Past the timeout the morph is no longer worth holding the navigation for: skip it (its
  // update callback still runs, promptly) and let the render go ahead.
  captureTimeoutId = setTimeout(() => {
    captureTimeoutId = null;
    if (!captureResolve) return;
    try {
      capturingTransition?.skipTransition?.();
    } catch {
      /* ignore */
    }
    markCaptured();
  }, CAPTURE_TIMEOUT_MS);
}

function markCaptured(): void {
  if (captureTimeoutId) {
    clearTimeout(captureTimeoutId); // so it can't release a later transition's wait
    captureTimeoutId = null;
  }
  const resolve = captureResolve;
  captureResolve = null;
  captureWait = null;
  capturingTransition = null;
  resolve?.();
}

/**
 * A morph decided on in +onPageTransitionStart but not started yet (Back, or a card click with no
 * preview seed). Starting it there froze the page under the old snapshot for the whole
 * pageContext fetch (~200 ms on prod, more on a cold Lambda) with no feedback at all; starting it
 * from +onRenderClient, once the next page's data is in hand, keeps the current page live (with
 * the navigation progress bar) during the fetch and still morphs straight into the new page.
 */
let deferredStart = false;

/**
 * Call from +onRenderClient before rendering a client-side navigation. Starts a deferred morph if
 * one is due, then resolves once the in-flight transition (if any) has captured its old snapshot;
 * resolves immediately when there's nothing to wait for. See captureWait.
 */
export function waitForJobCardTransitionCapture(): Promise<void> {
  if (deferredStart) {
    deferredStart = false;
    startPendingTransition(document as ViewTransitionCapableDocument);
  }
  return captureWait ?? Promise.resolve();
}

/** The morph whose update callback waits for +onPageTransitionEnd (resolveSpaJobCardTransition). */
function startPendingTransition(doc: ViewTransitionCapableDocument): void {
  if (typeof doc.startViewTransition !== 'function' || pendingResolve || captureWait) return;
  try {
    beginCaptureWait();
    const transition = doc.startViewTransition(
      () =>
        new Promise<void>((resolve) => {
          markCaptured();
          pendingResolve = resolve;
          // Safety net: never leave the transition (and the page's paint) hanging if
          // +onPageTransitionEnd doesn't fire for some reason (e.g. the navigation errors out).
          pendingTimeoutId = setTimeout(() => resolveSpaJobCardTransition(), 1500);
        }),
    );
    if (captureResolve) capturingTransition = transition;
    transition.finished.finally(clearSourceClasses);
    // A skipped transition (hidden tab, interrupted) rejects `ready`; that's expected, not an error.
    transition.ready?.catch(() => {});
  } catch {
    markCaptured();
    pendingResolve = null;
  }
}

function clearSourceClasses(): void {
  document
    .querySelectorAll(`article.${JOB_CARD_TRANSITION_SOURCE_CLASS}`)
    .forEach((el) => el.classList.remove(JOB_CARD_TRANSITION_SOURCE_CLASS));
}

/**
 * Call from +onPageTransitionStart.ts, before Vike swaps in the new page. See the file-level
 * comment above for the full picture.
 * - Armed card click with a registered preview seed (job-preview-shell.ts): mount the skeleton
 *   job page shell right away, inside the morph when the browser supports it, so the morph runs
 *   immediately instead of waiting for the server. The real page replaces the shell when ready.
 * - Otherwise (no seed, or Back away from the job page): a transition whose update callback
 *   waits for +onPageTransitionEnd, deferred until +onRenderClient has the next page's data (see
 *   deferredStart), so the current page isn't frozen during the fetch.
 * No-ops (leaving navigation untouched) when none of that applies.
 */
export function startSpaJobCardTransition(pageContext: {
  isBackwardNavigation?: boolean | null;
  urlPathname?: string | null;
}): void {
  if (typeof document === 'undefined') return;
  // A deferred morph from a navigation that never reached render (e.g. Back pressed mid-fetch)
  // must not leak into this one.
  deferredStart = false;
  const doc = document as ViewTransitionCapableDocument;
  const leavingJobPage = !!document.querySelector('.jc-job-page');
  const isBack = pageContext.isBackwardNavigation === true;
  const armedForward = !leavingJobPage && isArmedWithinWindow();
  const armedBack = leavingJobPage && isBack;
  const canAnimate =
    typeof doc.startViewTransition === 'function' && !prefersReducedMotion() && !pendingResolve && !captureWait;

  const previewSeed = armedForward ? takeJobPreviewSeedFor(pageContext.urlPathname) : null;
  if (previewSeed) {
    if (!canAnimate) {
      mountJobPreviewShell(previewSeed);
      return;
    }
    try {
      let finished: Promise<unknown> | null = null;
      const token = reserveJobPreviewShell();
      beginCaptureWait();
      const transition = doc.startViewTransition!(() => {
        markCaptured();
        mountJobPreviewShell(previewSeed, finished, token);
      });
      if (captureResolve) capturingTransition = transition;
      finished = transition.finished;
      transition.finished.finally(clearSourceClasses);
      // A skipped transition (hidden tab, interrupted) rejects `ready`; that's expected, not an error.
      transition.ready?.catch(() => {});
    } catch {
      markCaptured();
      mountJobPreviewShell(previewSeed);
    }
    return;
  }

  if (!canAnimate) return;
  if (!armedForward && !armedBack) return;
  // Started from +onRenderClient once the data is in (waitForJobCardTransitionCapture).
  deferredStart = true;
}

/** Call from +onPageTransitionEnd.ts, once Vike has finished rendering the new page. */
export function resolveSpaJobCardTransition(): void {
  if (!pendingResolve) return;
  // Coming back to the SRP: re-arm the matching card (same lookup the pagereveal guard above does
  // for a cross-document Back) so the "new" snapshot has a named element to morph back into.
  if (!document.querySelector('.jc-job-page')) {
    rearmSrpCardFromStorage();
  }
  const resolve = pendingResolve;
  pendingResolve = null;
  if (pendingTimeoutId) {
    clearTimeout(pendingTimeoutId);
    pendingTimeoutId = null;
  }
  resolve();
}
